import React from 'react'
import styled from 'styled-components'
import ReactModal from 'react-modal'

import breakpoints from '../../constants/breakpoints'
import i18n from '../../i18n'
import LinkButton from '../LinkButton'
import Icon from '../Icon'
import MoreInfoIcon from '../../assets/more-information.svg'
import Close from '../../assets/close.svg'

import { VideoType } from '../../types'
import Video from '../Video'

interface Props extends ReactModal.Props {
  className?: string
  isOpen: boolean
  onRequestClose: () => void
  title: string
  content: string
  videos?: VideoType[] | undefined
}

if (process.env.NODE_ENV !== 'test') ReactModal.setAppElement('#root')

const ReactModalAdapter: React.FC<Props> = ({
  className,
  isOpen,
  onRequestClose,
  title,
  content,
  videos
}) => {
  const contentClassName = `${className}__content`
  const overlayClassName = `${className}__overlay`

  return (
    <ReactModal
      className={contentClassName}
      overlayClassName={overlayClassName}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel={title}
      ariaHideApp={process.env.NODE_ENV !== 'test'}
      closeTimeoutMS={500}
    >
      <ContentWrapper>
        <Content className="text-formatted">
          <InfoIcon src={MoreInfoIcon} />
          <Title>{title}</Title>
          <TextContent dangerouslySetInnerHTML={{ __html: content }} />

          {videos &&
            videos.map((video, index) => (
              <MediaWrapper key={index}>{<Video {...video} />}</MediaWrapper>
            ))}

          <CloseButton onClick={onRequestClose}>
            <Icon icon={Close} />
            <span>{i18n.t('modal.close')}</span>
          </CloseButton>
        </Content>
      </ContentWrapper>
      <LinkButton onClick={onRequestClose}>{i18n.t('modal.goBack')}</LinkButton>
    </ReactModal>
  )
}

const InfoModal = styled(ReactModalAdapter)`
  &__overlay {
    position: fixed;
    z-index: 10;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ theme }) => theme.grey};
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
    transition-timing-function: ease !important;

    @media (min-width: ${breakpoints.md}) {
      padding: 30px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    max-width: 1000px;
    max-height: 100%;
    outline: none;
    overflow-x: hidden;
  }
`

const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  margin-bottom: 10px;

  @media (min-width: ${breakpoints.md}) {
    margin-bottom: 30px;
  }
`

const Content = styled.div`
  background-color: white;
  padding: 15px;
  overflow-wrap: break-word;
  max-height: 100%;
  overflow-y: auto;

  @media (min-width: ${breakpoints.md}) {
    padding: 45px;
  }
`

const TextContent = styled.div`
  img {
    display: block;
    width: 100%;
    max-height: 400px;
    margin: 32px auto 0;
    object-fit: contain;
  }
`

const Title = styled.h1`
  margin-top: 0;
  margin-right: 70px;
  font-size: 24px;

  @media (min-width: ${breakpoints.md}) {
    margin-right: 50px;
  }
`

const MediaWrapper = styled.div`
  max-width: 700px;
  margin: 0 auto;
`

const InfoIcon = styled.img`
  display: none;

  @media (min-width: ${breakpoints.sm}) {
    display: inline;
    float: left;
    margin-right: 15px;
    width: 30px;
    height: 30px;
  }
`

const CloseButton = styled.button`
  display: inline-flex;
  padding: 0;
  font-size: 15px;
  position: absolute;
  top: 15px;
  right: 15px;
  background: 0;
  border: 0;
  outline: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.warmGrey};

  @media (min-width: ${breakpoints.md}) {
    top: 35px;
    right: 35px;
  }

  & svg {
    display: inline-block;
    width: 10px;
    height: 10px;
    fill: ${({ theme }) => theme.warmGrey};
    margin-right: 7px;
  }
`

export default InfoModal
